import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../..';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { PartnerCarrierRate } from '@shipium-corp/shipium-ts-types';

export interface CarrierRatesModel {
  carrierRatesMap: Record<string, PartnerCarrierRate>;
  setCarrierRates: Action<CarrierRatesModel, PartnerCarrierRate[]>;
  setCarrierRate: Action<CarrierRatesModel, PartnerCarrierRate>;
  fetchCarrierRatesLoading: boolean;
  setFetchCarrierRatesLoading: Action<CarrierRatesModel, boolean>;
  fetchCarrierRates: Thunk<CarrierRatesModel, void, Injections, StoreModel, Promise<void>>;
  fetchCarrierRatesFailure: Action<CarrierRatesModel, ApplicationErrorOptions>;
  updateCarrierRateCurrency: Thunk<
    CarrierRatesModel,
    { carrierRateSetId: string; currencyCode: string },
    Injections,
    StoreModel,
    Promise<any | Error>
  >;
  archiveCarrierRate: Thunk<CarrierRatesModel, string, Injections, StoreModel, Promise<void>>;
  archiveCarrierRatesBulk: Thunk<CarrierRatesModel, string[], Injections, StoreModel, Promise<void>>;
}

export const carrierRatesModel: CarrierRatesModel = {
  carrierRatesMap: {},
  setCarrierRates: action((state, carrierRates) => {
    const carrierRatesMap = carrierRates.reduce<Record<string, PartnerCarrierRate>>((acc, curr) => {
      acc[curr.carrierRateId] = curr;
      return acc;
    }, {});
    state.carrierRatesMap = carrierRatesMap;
  }),
  setCarrierRate: action((state, carrierRate) => {
    state.carrierRatesMap[carrierRate.carrierRateId] = carrierRate;
  }),
  fetchCarrierRatesLoading: false,
  setFetchCarrierRatesLoading: action((state, isLoading) => {
    state.fetchCarrierRatesLoading = isLoading;
  }),
  fetchCarrierRates: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchCarrierRatesLoading(true);
      const { assetService } = injections;
      const response = await assetService.fetchCarrierRates({ carrierRateType: 'contract' });
      actions.setCarrierRates(response as PartnerCarrierRate[]);
      actions.setFetchCarrierRatesLoading(false);
      return response;
    } catch (error) {
      actions.fetchCarrierRatesFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchCarrierRatesLoading(false);
      return error;
    }
  }),
  fetchCarrierRatesFailure: action((state, error) => {
    console.error(error);
  }),
  updateCarrierRateCurrency: thunk(async (actions, payload, { injections }) => {
    try {
      actions.setFetchCarrierRatesLoading(true);
      const res = await injections.assetService.updateCarrierRateCurrency(
        payload.carrierRateSetId,
        payload.currencyCode,
      );
      return res;
    } catch (error) {
      actions.fetchCarrierRatesFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message || 'An error occured updating carrier rate currency.',
        asNotification: true,
      });
      actions.setFetchCarrierRatesLoading(false);
      return error;
    }
  }),
  archiveCarrierRate: thunk(async (actions, carrierRateId, { injections }) => {
    const { assetService } = injections;
    await assetService.archiveCarrierRate(carrierRateId);
    await actions.fetchCarrierRates();
  }),
  archiveCarrierRatesBulk: thunk(async (actions, carrierRateIds, { injections }) => {
    const { assetService } = injections;
    await Promise.all(carrierRateIds.map((id) => assetService.archiveCarrierRate(id)));
    await actions.fetchCarrierRates();
  }),
};

export default carrierRatesModel;
